import { EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS } from "@repo/caip";
import { z } from "zod";

const rawAmount = z.string().regex(/^(?:0|[1-9]\d*)$/u);
const chainRef = z
  .string()
  .regex(/^eip155:[1-9]\d*$/u)
  .refine((value) =>
    Number.isSafeInteger(Number(value.slice("eip155:".length))),
  );
const accountRef = z.string().regex(/^eip155:[1-9]\d*:0x[0-9a-f]{40}$/u);
const assetId = z
  .string()
  .regex(/^eip155:[1-9]\d*\/(?:erc20:0x[0-9a-f]{40}|slip44:60)$/u)
  .refine(
    (value) =>
      !value.endsWith(`/erc20:${EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS}`),
    "Native assets must use slip44:60",
  );
const scope = {
  portfolioId: z.string().trim().min(1).max(256),
  accountRef,
  assetId,
};
const sameChain = (value: { accountRef: string; assetId: string }) => {
  const accountChain = value.accountRef.slice(
    0,
    value.accountRef.lastIndexOf(":"),
  );
  return (
    chainRef.safeParse(accountChain).success &&
    value.assetId.startsWith(`${accountChain}/`)
  );
};

/** Callers select scope only. Execution acquires every financial fact. */
export const SellBalanceInputSchema = z
  .object(scope)
  .strict()
  .refine(sameChain, {
    message: "The account and asset must use the same canonical EVM chain",
  });
export type SellBalanceInput = z.infer<typeof SellBalanceInputSchema>;

const observation = {
  ...scope,
  chainRef,
  capturedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  blockNumber: rawAmount,
};

/** Advisory live raw balance; order admission still reacquires financial authority. */
export const SellBalanceSchema = z
  .discriminatedUnion("status", [
    z
      .object({
        ...observation,
        status: z.literal("ready"),
        onchainRaw: rawAmount,
        heldRaw: rawAmount,
        spendableRaw: rawAmount,
      })
      .strict(),
    z
      .object({
        ...observation,
        status: z.literal("unavailable"),
        reason: z.enum([
          "source_debit_block_unproven",
          "pinned_block_before_source_debit",
        ]),
      })
      .strict(),
  ])
  .refine(
    (value) =>
      sameChain(value) && value.assetId.startsWith(`${value.chainRef}/`),
    {
      message: "Observation identities must share one canonical EVM chain",
    },
  )
  .refine(
    (value) =>
      Date.parse(value.expiresAt) === Date.parse(value.capturedAt) + 30_000,
    {
      message: "Sell balance observations expire after 30 seconds",
    },
  )
  .refine(
    (value) => {
      if (value.status !== "ready") return true;
      if (
        ![value.onchainRaw, value.heldRaw, value.spendableRaw].every(
          (raw) => rawAmount.safeParse(raw).success,
        )
      )
        return false;
      const onchain = BigInt(value.onchainRaw);
      const held = BigInt(value.heldRaw);
      return (
        BigInt(value.spendableRaw) === (onchain > held ? onchain - held : 0n)
      );
    },
    { message: "Spendable balance must equal the balance after holds" },
  );
export type SellBalance = z.infer<typeof SellBalanceSchema>;
